import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Row,
  Col,
  Card,
  Space,
  Statistic,
  Table,
  Typography,
  Button,
  Tag,
  message,
  Modal,
  Form,
  Input,
  Select
} from 'antd';
import { api } from './api';
import {
  formatAmount,
  statusLabelMap,
  statusTagColors,
  languageOptions,
  formatCvrNumber
} from './constants';

const { Title, Text, Paragraph } = Typography;

type ReferrerStats = {
  totalLeads: number;
  activeLeads: number;
  signedLeads: number;
  paidOutLeads: number;
  totalContractValue: number;
  potentialCommission: number;
};

type ReferrerOverview = {
  id: string;
  fullName: string | null;
  email: string;
  phone: string | null;
  company: string | null;
  cvrnummer: string | null;
  address: string | null;
  notes: string | null;
  preferredLanguage: string | null;
  stats: ReferrerStats;
};

type LeadSummary = {
  id: string;
  company_name: string;
  status: string;
  contract_value_annual: number | null;
  commission_percent: number | null;
  currency: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  created_at: string;
  last_updated: string;
};

type AdminReferrersPageProps = {
  onOpenLead?: (leadId: string) => void;
};

function formatDate(value: string | null | undefined) {
  if (!value) return '-';
  try {
    return new Intl.DateTimeFormat('da-DK', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(new Date(value));
  } catch {
    return value;
  }
}

export default function AdminReferrersPage({ onOpenLead }: AdminReferrersPageProps) {
  const [loading, setLoading] = useState(false);
  const [overview, setOverview] = useState<ReferrerOverview[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<ReferrerOverview | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [leads, setLeads] = useState<LeadSummary[]>([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [form] = Form.useForm();

  const totals = useMemo(() => {
    return overview.reduce(
      (acc, item) => {
        acc.totalLeads += item.stats.totalLeads;
        acc.activeLeads += item.stats.activeLeads;
        acc.signedLeads += item.stats.signedLeads;
        acc.paidOutLeads += item.stats.paidOutLeads;
        acc.totalContractValue += item.stats.totalContractValue;
        acc.potentialCommission += item.stats.potentialCommission;
        return acc;
      },
      {
        totalLeads: 0,
        activeLeads: 0,
        signedLeads: 0,
        paidOutLeads: 0,
        totalContractValue: 0,
        potentialCommission: 0
      }
    );
  }, [overview]);

  const loadOverview = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api('/api/referrers/overview');
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Kunne ikke hente referrer-data');
      }
      const data = (await res.json()) as ReferrerOverview[];
      setOverview(data);
      if (data.length === 0) {
        setSelectedId(null);
        setDetail(null);
        setLeads([]);
        return;
      }
      const currentExists = selectedId ? data.some((item) => item.id === selectedId) : false;
      if (!selectedId || !currentExists) {
        setSelectedId(data[0].id);
      }
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke hente referrers');
    } finally {
      setLoading(false);
    }
  }, [selectedId]);

  async function loadDetail(id: string) {
    setDetailLoading(true);
    try {
      const res = await api(`/api/referrers/${id}`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Kunne ikke hente detaljer');
      }
      const data = (await res.json()) as ReferrerOverview;
      setDetail(data);
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke hente detaljer');
    } finally {
      setDetailLoading(false);
    }
  }

  async function loadLeadsFor(id: string) {
    setLeadsLoading(true);
    try {
      const res = await api(`/api/referrers/${id}/leads`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Kunne ikke hente leads for referrer');
      }
      const data = (await res.json()) as LeadSummary[];
      setLeads(data);
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke hente referrer-leads');
    } finally {
      setLeadsLoading(false);
    }
  }

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  useEffect(() => {
    if (!selectedId) {
      return;
    }
    loadDetail(selectedId);
    loadLeadsFor(selectedId);
  }, [selectedId]);

  const columns = [
    {
      title: 'Navn',
      dataIndex: 'fullName',
      key: 'name',
      render: (_: any, record: ReferrerOverview) => record.fullName || record.email
    },
    {
      title: 'E-mail',
      dataIndex: 'email',
      key: 'email'
    },
    {
      title: 'CVR',
      dataIndex: 'cvrnummer',
      key: 'cvrnummer',
      render: (value: string | null) => (value ? formatCvrNumber(value) : '—')
    },
    {
      title: 'Virksomhed',
      dataIndex: 'company',
      key: 'company',
      render: (value: string | null) => value || '—'
    },
    {
      title: 'Leads',
      key: 'totalLeads',
      render: (_: any, record: ReferrerOverview) => record.stats.totalLeads
    },
    {
      title: 'Underskrevet',
      key: 'signedLeads',
      render: (_: any, record: ReferrerOverview) => record.stats.signedLeads
    },
    {
      title: 'Potentiel provision',
      key: 'potentialCommission',
      render: (_: any, record: ReferrerOverview) =>
        formatAmount(record.stats.potentialCommission, 'DKK')
    }
  ];

  function openEditModal() {
    if (!detail) return;
    setEditOpen(true);
  }

  const applyDetailToForm = useCallback(() => {
    if (!detail) {
      return;
    }
    form.setFieldsValue({
      full_name: detail.fullName || '',
      phone: detail.phone || '',
      company: detail.company || '',
      address: detail.address || '',
      notes: detail.notes || '',
      preferred_language: detail.preferredLanguage || undefined
    });
  }, [detail, form]);

  useEffect(() => {
    if (editOpen) {
      applyDetailToForm();
    }
  }, [applyDetailToForm, editOpen]);

  async function handleEditSubmit() {
    if (!selectedId) return;
    try {
      const values = await form.validateFields();
      const payload = {
        full_name: values.full_name?.trim(),
        phone: values.phone?.trim() || null,
        company: values.company?.trim() || null,
        address: values.address?.trim() || null,
        notes: values.notes?.trim() || null,
        preferred_language: values.preferred_language || null
      };
      setEditSubmitting(true);
      const res = await api(`/api/referrers/${selectedId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Kunne ikke opdatere referrer');
      }
      const updated = (await res.json()) as ReferrerOverview;
      setDetail(updated);
      await loadOverview();
      message.success('Referrer opdateret');
      setEditOpen(false);
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      message.error(error?.message || 'Kunne ikke opdatere referrer');
    } finally {
      setEditSubmitting(false);
    }
  }

  return (
    <Space direction="vertical" size={24} style={{ width: '100%' }}>
      <Row gutter={16}>
        <Col xs={12} md={6}>
          <Card bordered={false}>
            <Statistic title="Referrers" value={overview.length} loading={loading} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card bordered={false}>
            <Statistic
              title="Leads i alt"
              value={totals.totalLeads}
              loading={loading}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card bordered={false}>
            <Statistic
              title="Aktive leads"
              value={totals.activeLeads}
              loading={loading}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card bordered={false}>
            <Statistic
              title="Potentiel provision"
              valueRender={() => <span>{formatAmount(totals.potentialCommission, 'DKK')}</span>}
              loading={loading}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} lg={12}>
          <Card title="Referrer-oversigt" bodyStyle={{ padding: 0 }}>
            <Table
              rowKey="id"
              dataSource={overview}
              columns={columns}
              loading={loading}
              pagination={false}
              onRow={(record) => ({
                onClick: () => setSelectedId(record.id)
              })}
              rowClassName={(record) => (record.id === selectedId ? 'selected-row' : '')}
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card
            title="Detaljer"
            extra={
              detail && (
                <Button onClick={openEditModal} type="primary">
                  Rediger referrer
                </Button>
              )
            }
            loading={detailLoading && !detail}
          >
            {detail ? (
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <div>
                  <Title level={4} style={{ marginBottom: 0 }}>
                    {detail.fullName || detail.email}
                  </Title>
                  <Text type="secondary">{detail.email}</Text>
                </div>
                <Space direction="vertical" size={4}>
                  <Text>
                    <strong>Telefon:</strong> {detail.phone || '—'}
                  </Text>
                  <Text>
                    <strong>CVR:</strong> {detail.cvrnummer ? formatCvrNumber(detail.cvrnummer) : '—'}
                  </Text>
                  <Text>
                    <strong>Virksomhed:</strong> {detail.company || '—'}
                  </Text>
                  <Text>
                    <strong>Adresse:</strong> {detail.address || '—'}
                  </Text>
                  <Text>
                    <strong>Foretrukket sprog:</strong> {detail.preferredLanguage || '—'}
                  </Text>
                </Space>
                {detail.notes && (
                  <Paragraph style={{ whiteSpace: 'pre-line' }}>
                    <strong>Noter:</strong> {detail.notes}
                  </Paragraph>
                )}
                <Row gutter={16}>
                  <Col span={12}>
                    <Statistic title="Leads" value={detail.stats.totalLeads} />
                  </Col>
                  <Col span={12}>
                    <Statistic title="Aktive leads" value={detail.stats.activeLeads} />
                  </Col>
                  <Col span={12}>
                    <Statistic title="Underskrevet" value={detail.stats.signedLeads} />
                  </Col>
                  <Col span={12}>
                    <Statistic title="Udbetalte" value={detail.stats.paidOutLeads} />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="Kontraktværdi"
                      valueRender={() => (
                        <span>{formatAmount(detail.stats.totalContractValue, 'DKK')}</span>
                      )}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="Potentiel provision"
                      valueRender={() => (
                        <span>{formatAmount(detail.stats.potentialCommission, 'DKK')}</span>
                      )}
                    />
                  </Col>
                </Row>
              </Space>
            ) : (
              <Text type="secondary">Vælg en referrer for at se detaljer</Text>
            )}
          </Card>
        </Col>
      </Row>

      <Card title="Leads" loading={leadsLoading && !leads.length}>
        <Table
          rowKey="id"
          dataSource={leads}
          loading={leadsLoading}
          pagination={{ pageSize: 6 }}
          onRow={(record) => ({
            onClick: onOpenLead ? () => onOpenLead(record.id) : undefined,
            style: onOpenLead ? { cursor: 'pointer' } : undefined
          })}
          columns={[
            {
              title: 'Virksomhed',
              dataIndex: 'company_name',
              key: 'company_name'
            },
            {
              title: 'Status',
              dataIndex: 'status',
              key: 'status',
              render: (value: string) => {
                const label = statusLabelMap.get(value) || value;
                const color = statusTagColors[value] || 'default';
                return <Tag color={color}>{label}</Tag>;
              }
            },
            {
              title: 'Kontraktværdi',
              key: 'contract',
              render: (_: any, record: LeadSummary) =>
                formatAmount(record.contract_value_annual, record.currency || 'DKK')
            },
            {
              title: 'Provision (%)',
              dataIndex: 'commission_percent',
              key: 'commission_percent',
              render: (value: number | null) => (value ?? '-')
            },
            {
              title: 'Kontaktperson',
              key: 'contact',
              render: (_: any, record: LeadSummary) =>
                record.contact_name || record.contact_email || record.contact_phone || '—'
            },
            {
              title: 'Oprettet',
              dataIndex: 'created_at',
              key: 'created_at',
              render: (value: string) => formatDate(value)
            },
            {
              title: 'Opdateret',
              dataIndex: 'last_updated',
              key: 'last_updated',
              render: (value: string) => formatDate(value)
            }
          ]}
          locale={{ emptyText: selectedId ? 'Ingen leads for referreren endnu' : 'Vælg en referrer' }}
        />
      </Card>

      <Modal
        title={detail ? `Rediger ${detail.fullName || detail.email}` : 'Rediger referrer'}
        open={editOpen}
        onCancel={() => setEditOpen(false)}
        onOk={handleEditSubmit}
        confirmLoading={editSubmitting}
        afterOpenChange={(open) => {
          if (open) {
            form.resetFields();
            applyDetailToForm();
          } else {
            form.resetFields();
          }
        }}
        destroyOnClose
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item
            name="full_name"
            label="Navn"
            rules={[{ required: true, message: 'Angiv referrerens navn' }]}
          >
            <Input placeholder="Fulde navn" />
          </Form.Item>
          <Form.Item name="phone" label="Telefon">
            <Input placeholder="F.eks. +45 12 34 56 78" />
          </Form.Item>
          <Form.Item name="company" label="Virksomhed">
            <Input placeholder="Virksomhedsnavn" />
          </Form.Item>
          <Form.Item name="address" label="Adresse">
            <Input placeholder="Adresse" />
          </Form.Item>
          <Form.Item name="preferred_language" label="Foretrukket sprog">
            <Select allowClear options={languageOptions} placeholder="Vælg sprog" />
          </Form.Item>
          <Form.Item name="notes" label="Noter">
            <Input.TextArea rows={3} placeholder="Evt. interne noter" />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
}
